/* File: frontend/src/App.js */
/* Version: 2.1 (2025-12-17) */
/* Description: Connected real ClientProfilePage and updated route params. */

import React, { Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider, useBranding } from './context/BrandingContext'; // [Phase 07.1] Tenant identity + theme gating
import { paralegalSkryt } from './utils/paralegalScreen';
import { ReputationTasksProvider } from './context/ReputationTasksContext'; // [NEW]
import { UiLanguageProvider, useUiLanguage } from './context/UiLanguageContext'; // [Phase 9] язык интерфейса по признаку арендатора
import { appTheme } from './theme'; // [Phase I.3] Unified MUI theme based on semantic tokens

// --- 2. ИМПОРТ КОМПОНЕНТОВ И СТРАНИЦ ---
import Navbar from './components/Navbar';
import SectionSubnav from './components/SectionSubnav'; // [quick-260908-pun] полоса подраздела fitenko
import LoadingSpinner from './components/LoadingSpinner';
import PublicChatWidget from './components/PublicChatWidget';
import KatifiWidget from './components/KatifiWidget'; // [NEW] Phase 11
import PublicCalendarWidget from './components/PublicCalendarWidget'; // [NEW]
import InboxWorkspace from './components/InboxWorkspace'; // [NEW] Sprint 1 Omnichannel Inbox
import AIOperationsHub from './components/AIOperationsHub'; // [NEW] Sprint 3 AI Operations Hub
import WidgetLayout from './layouts/WidgetLayout';
import WidgetPage from './pages/WidgetPage';
import CommandPalette from './components/CommandPalette';
import Button from './components/ui/button';

// Основные страницы
import Home from './pages/Home';
import Login from './pages/Login';
import DashboardPage from './pages/Dashboard';
import AIParalegal from './pages/AIParalegal';
import DocumentsPage from './pages/DocumentsPage';
import ReputationDashboard from './pages/ReputationDashboard';
import BlogPage from './pages/BlogPage'; // [Часть A] Blog Pipeline Dashboard
import UserManagement from './components/UserManagement'; // Обратите внимание: это компонент, но используется как страница
import IntakeFormPage from './pages/IntakeFormPage';
import NewCasePage from './pages/NewCasePage';
import CaseDetailPage from './pages/CaseDetailPage';
import CasesPage from './pages/CasesPage';
import LeadHistoryPage from './pages/LeadHistoryPage';
import HistoryPage from './pages/HistoryPage'; // [quick-260909-v7b] экран «История» арендатора fitenko
import LeadsPage from './pages/LeadsPage';
import EmailTriagePage from './pages/EmailTriagePage'; // [NEW] Email Inbox (Email Triage)
import CalendarPage from './pages/CalendarPage';
import UserProfileAdmin from './pages/UserProfileAdmin';

// === [NEW] Импорт новой страницы профиля клиента ===
import ClientProfilePage from './pages/ClientProfilePage';
import AdminBrainCenter from './pages/AdminBrainCenter'; // [NEW] Brain Center
import VoicePage from './pages/VoicePage'; // [NEW] AI Voice Paralegal
import LiveMonitorPage from './pages/LiveMonitorPage'; // [NEW] Live Monitor
import TasksPage from './pages/TasksPage'; // [Protocol v8.0] AI Task Center
import OrganicSEOPage from './pages/organic-seo/OrganicSEOPage'; // [NEW] Organic SEO
import AiStaffPage from './pages/AiStaffPage'; // [NEW] AI Staff Dashboard
import MagicExecutorPage from './pages/MagicExecutorPage'; // [NEW] Magic URL Executor
import AuditLogPage from './pages/AuditLogPage'; // [NEW] Audit Log Dashboard
import SeoAnalyticsPage from './pages/SeoAnalyticsPage'; // [NEW] SEO Analytics Dashboard
import ClientPortalPage from './pages/ClientPortalPage'; // [Phase 05] Client Portal
import DocumentGeneratorPage from './pages/DocumentGeneratorPage'; // [Phase 05] Document Generator
import AdminInstanceProfile from './pages/AdminInstanceProfile'; // [Phase 06.1] Creator-only instance profile editor
import UiKitShowcasePage from './pages/UiKitShowcasePage'; // [Phase I.4] Creator-only UI Kit Showcase
// ==================================================
// ==================================================

// --- 3. КОМПОНЕНТЫ-ЗАГЛУШКИ ---
const CaseAnalysisPage = () => <div className="container card"><h2>Case Analysis Page (Placeholder)</h2><p>This page will show the detailed AI analysis of a case.</p></div>;
// TasksPage imported from ./pages/TasksPage.js
const TaskDetailPage = () => {
    const { t } = useUiLanguage();
    return (
        <div className="container card">
            <h2>{t('practice.tasks.task_detail_title', 'Task Detail Page (Placeholder)')}</h2>
            <p>{t('practice.tasks.task_detail_subtitle', 'This page will show the details of a specific task.')}</p>
        </div>
    );
};

// (Заглушка ClientProfilePage удалена, теперь используется реальный файл)

// --- 4. КОМПОНЕНТ ЗАЩИЩЕННОГО МАКЕТА ---
const ProtectedLayout = () => {
    const { user } = useAuth();
    const { isFitenko, features } = useBranding();
    const isRail = isFitenko || features?.shell === 'rail';
    return (
        <div className="App">
            <Navbar />
            {isRail && <SectionSubnav />}   {/* ДОСТИЖИМОСТЬ СВЁРНУТОГО */}
            <CommandPalette />
            <main className="main-content">
                <Outlet />
            </main>
        </div>
    );
};

// Защита маршрута AI Paralegal: скрыт, если выключен признаком арендатора (ELN)
const AIParalegalRoute = () => {
    const { features } = useBranding();
    if (paralegalSkryt(features)) {
        return <Navigate to="/dashboard" replace />;
    }
    return <AIParalegal />;
};

// --- 5. КОМПОНЕНТ С МАРШРУТАМИ ---
const AppRoutes = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return <LoadingSpinner message="Verifying authentication..." />;
    }

    return (
        <Routes>
            {/* Публичные маршруты */}
            <Route path="/" element={<Home />} />
            <Route
                path="/login"
                element={user ? <Navigate to="/dashboard" /> : <Login />}
            />

            <Route path="/widget" element={
                <WidgetLayout>
                    <WidgetPage />
                </WidgetLayout>
            } />

            {/* Защищенные маршруты */}
            <Route
                element={user ? <ProtectedLayout /> : <Navigate to="/login" />}
            >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/ai-paralegal" element={<AIParalegalRoute />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/reputation" element={<ReputationDashboard />} />
                <Route path="/blog" element={<BlogPage />} />

                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/users" element={<Navigate to="/user-management" replace />} />

                <Route path="/leads" element={<LeadsPage />} />
                <Route path="/leads/:id" element={<LeadHistoryPage />} />
                {/* [quick-260909-v7b] РОВНО ОДИН новый маршрут. Выбранный человек
                    живёт в строке адреса как ?lead=<id>, а не отдельным путём:
                    экран остаётся один и делится ссылкой. */}
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/inbox" element={<InboxWorkspace />} /> {/* [NEW] Sprint 1 Omnichannel Inbox */}
                <Route path="/ai-ops" element={<AIOperationsHub />} /> {/* [NEW] Sprint 3 AI Operations Hub */}
                <Route path="/email-inbox" element={<EmailTriagePage />} /> {/* [NEW] Email Inbox (Email Triage) */}
                <Route path="/ai-staff" element={<AiStaffPage />} />
                <Route path="/magic-executor" element={<MagicExecutorPage />} />
                <Route path="/cases" element={<CasesPage />} />
                <Route path="/cases/new" element={<NewCasePage />} />
                <Route path="/cases/:caseId" element={<CaseDetailPage />} />
                <Route path="/cases/:caseId/intake" element={<IntakeFormPage />} />
                <Route path="/cases/:caseId/analysis" element={<CaseAnalysisPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/tasks/:taskId" element={<TaskDetailPage />} />

                {/* === [UPDATED] Маршрут к профилю клиента === */}
                {/* Используем :id, чтобы совпадало с useParams в ClientProfilePage.js */}
                <Route path="/clients/:id" element={<ClientProfilePage />} />
                {/* =========================================== */}

                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/profile" element={<UserProfileAdmin />} />
                <Route path="/admin/brain" element={<AdminBrainCenter />} /> {/* [NEW] Brain Center Route */}
                <Route path="/voice" element={<VoicePage />} /> {/* [NEW] AI Voice Paralegal Route */}
                <Route path="/monitor" element={<LiveMonitorPage />} /> {/* [NEW] Live Monitor Route */}
                <Route path="/organic-seo" element={<OrganicSEOPage />} /> {/* [NEW] Organic SEO Route */}
                <Route path="/seo-analytics" element={<SeoAnalyticsPage />} /> {/* [NEW] SEO Analytics Dashboard */}
                <Route path="/admin/audit-log" element={<AuditLogPage />} /> {/* [NEW] Audit Log Route */}
                <Route path="/client-portal" element={<ClientPortalPage />} /> {/* [Phase 05] Client Portal */}
                <Route path="/document-generator" element={<DocumentGeneratorPage />} /> {/* [Phase 05] Document Generator */}
                <Route path="/admin/company-profile" element={<AdminInstanceProfile />} /> {/* [Phase 06.1] Creator-only instance profile editor */}
                <Route path="/admin/ui-kit" element={<UiKitShowcasePage />} /> {/* [Phase I.4] Creator-only UI Kit Showcase */}

            </Route>

            {/* [NEW] Embed Route for Calendar */}
            <Route path="/embed/calendar" element={<PublicCalendarWidget />} />



            {/* "Catch-all" маршрут */}
            <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} />} />
        </Routes>
    );
};

// --- 6. ERROR BOUNDARY ---
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        console.error('App ErrorBoundary:', error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'var(--font-ui, sans-serif)', color: 'var(--text)' }}>
                    <h2 style={{ color: 'var(--danger, #dc2626)', fontFamily: 'var(--font-display, inherit)', fontSize: '1.5rem', marginBottom: '8px' }}>Something went wrong</h2>
                    <p style={{ color: 'var(--text-muted, #6b7280)', marginTop: '8px', fontSize: '0.9375rem' }}>{this.state.error?.message}</p>
                    <div style={{ marginTop: '20px' }}>
                        <Button
                            variant="primary"
                            onClick={() => window.location.reload()}
                        >
                            Reload Page
                        </Button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// --- 7. ГЛАВНЫЙ КОМПОНЕНТ ---
export const AppWrapper = () => {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    const isEmbed = location.pathname.startsWith('/embed');
    const isLogin = location.pathname.startsWith('/login');
    const isWidget = location.pathname.startsWith('/widget');

    return (
        <>
            <AppRoutes />
            {/* Global Katifi Chat Widget */}
            {isAuthenticated && !isEmbed && <KatifiWidget />}

            {/* Hide chat widget if user is logged in, on an embed page, or on the login page */}
            {!isAuthenticated && !isEmbed && !isLogin && !isWidget && <PublicChatWidget />}
        </>
    );
};

function App() {
    // [Phase 07.1] AppShell reads useBranding() to pick MUI theme per tenant.
    // Must be a CHILD of BrandingProvider — useBranding() returns the default before
    // the provider mounts otherwise.
    const AppShell = () => {
        const { isFitenko, features, loading, failed } = useBranding();
        const theme = appTheme;
        // 🔴 Пока сервер не назвал арендатора — экран подключения, а не оболочка по
        //    умолчанию: 17.10.2026 при простое бэкенда Алан увидел «MLG OS».
        //    Язык ещё неизвестен (он в /branding), поэтому подпись на двух языках.
        if (loading) {
            return (
                <div data-branding-connecting style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F7F2', color: '#20231F', fontFamily: 'system-ui, -apple-system, sans-serif', textAlign: 'center', padding: 24 }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600 }}>Connecting to the server… · Подключаемся к серверу…</div>
                        {failed && (
                            <div style={{ fontSize: 13, color: '#666D74', marginTop: 8 }}>
                                The server is not responding yet — retrying automatically. · Сервер пока не отвечает — повторяем автоматически.
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <ThemeProvider theme={theme}>
                <Router>
                    <AuthProvider>
                        <ReputationTasksProvider>
                            <Toaster position="bottom-right" />
                            <AppWrapper />
                        </ReputationTasksProvider>
                    </AuthProvider>
                </Router>
            </ThemeProvider>
        );
    };

    return (
        <ErrorBoundary>
            {/* StyledEngineProvider injectFirst — MUST be injectFirst so Tailwind utilities
                (specificity 1) can override MUI defaults without !important.
                See RESEARCH §"Pitfall 1". */}
            <StyledEngineProvider injectFirst>
                <BrandingProvider>
                    <UiLanguageProvider>
                        <AppShell />
                    </UiLanguageProvider>
                </BrandingProvider>
            </StyledEngineProvider>
        </ErrorBoundary>
    );
}

export default App;